import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Volume1 } from "lucide-react";

/**
 * Geneo AI · Sound Control
 *
 * Two visual variants:
 *   variant="card"     — for the Motion card (compact horizontal, expanding slider)
 *   variant="overlay"  — floating pill for video players (top-right)
 *
 * Props:
 *   soundOn         : boolean          — mute state
 *   volume          : number (0..1)    — playback volume
 *   onToggle()      : flip mute
 *   onVolumeChange(v: number)          : new volume
 *   accent          : "cyan" | "gold"  — hue emphasis (default cyan)
 *   testId          : string           — data-testid namespace
 */
export const SoundControl = ({
  soundOn,
  volume,
  onToggle,
  onVolumeChange,
  variant = "card",
  accent = "cyan",
  testId = "sound-control",
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Auto-open on enable, auto-close on mute (with delay for animation)
  useEffect(() => {
    if (soundOn) setOpen(true);
    else setOpen(false);
  }, [soundOn]);

  // Close overlay slider when clicking outside (overlay variant only)
  useEffect(() => {
    if (variant !== "overlay" || !open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(soundOn); // keep open if sound on
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [variant, open, soundOn]);

  const accentText = accent === "gold" ? "text-[#F2C94C]" : "text-[#00E5FF]";
  const accentBorder = accent === "gold" ? "border-[#F2C94C]" : "border-[#00E5FF]";
  const accentGlow = accent === "gold" ? "shadow-[0_0_18px_-4px_rgba(242,201,76,0.55)]" : "shadow-[0_0_18px_-4px_rgba(0,229,255,0.55)]";
  const accentFillHex = accent === "gold" ? "#F2C94C" : "#00E5FF";

  const Icon = !soundOn ? VolumeX : volume < 0.35 ? Volume1 : Volume2;

  // Percentage label
  const pct = Math.round((volume ?? 0) * 100);

  if (variant === "overlay") {
    return (
      <div
        ref={wrapRef}
        className={`absolute top-3 right-3 z-20 flex items-center gap-2 rounded-full bg-[#0A0A0A]/85 backdrop-blur border ${soundOn ? accentBorder : "border-[#605C50]/40"} p-1 pl-1.5 transition-all ${soundOn ? accentGlow : ""}`}
        onMouseEnter={() => soundOn && setOpen(true)}
        data-testid={`${testId}-overlay`}
      >
        {/* Expanding slider (mount when soundOn) */}
        <div
          className={`overflow-hidden flex items-center transition-[width,opacity] duration-300 ease-out ${soundOn && open ? "w-24 opacity-100 pr-2" : "w-0 opacity-0 pr-0"}`}
          aria-hidden={!(soundOn && open)}
        >
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={pct}
            onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
            aria-label="Volume"
            data-testid={`${testId}-slider`}
            className="geneo-volume-slider w-full"
            style={{ accentColor: accentFillHex, "--fill": `${pct}%`, "--accent": accentFillHex }}
          />
        </div>

        {/* Toggle button */}
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={soundOn}
          aria-label={soundOn ? "Mute sound" : "Enable sound"}
          data-testid={`${testId}-toggle`}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            soundOn
              ? `bg-gradient-to-br from-[#F2C94C] to-[#00E5FF] text-[#0A0A0A]`
              : "bg-[#120E15] border border-[#605C50]/50 text-white hover:text-[#F2C94C] hover:border-[#F2C94C]"
          }`}
        >
          <Icon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // === CARD variant ===
  return (
    <div
      className="flex items-center justify-between gap-3 pt-3 border-t border-[#00E5FF]/10"
      data-testid={`${testId}-card`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={soundOn}
          aria-label={soundOn ? "Mute sound" : "Enable sound"}
          data-testid={`${testId}-toggle`}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all border ${
            soundOn
              ? `bg-gradient-to-br from-[#F2C94C]/20 to-[#00E5FF]/20 ${accentBorder} ${accentText} ${accentGlow}`
              : "bg-[#0A0A0A] border-[#605C50]/50 text-white hover:text-[#F2C94C] hover:border-[#F2C94C]"
          }`}
        >
          <Icon className="w-4 h-4" />
        </button>
        <div className="min-w-0">
          <div className="font-mono-hy text-[10px] text-white">Sound Effects</div>
          <div className="font-mono-hy text-[9px] text-white truncate">
            {soundOn ? `Playback · ${pct}%` : "Muted playback"}
          </div>
        </div>
      </div>
      <div
        className={`flex items-center gap-2 overflow-hidden transition-[width,opacity] duration-300 ease-out ${soundOn ? "w-40 opacity-100" : "w-0 opacity-0"}`}
        aria-hidden={!soundOn}
      >
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={pct}
          onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
          aria-label="Volume"
          data-testid={`${testId}-slider`}
          className="geneo-volume-slider w-full"
          style={{ accentColor: accentFillHex, "--fill": `${pct}%`, "--accent": accentFillHex }}
        />
        <span className={`font-mono-hy text-[9px] ${accentText} tabular-nums w-8 text-right`}>{pct}%</span>
      </div>
    </div>
  );
};

/**
 * Hook that keeps a video element's DOM `.volume` synced with React state.
 * (React does not forward the `volume` prop to <video> elements.)
 */
export const useVideoVolume = (ref, volume, soundOn) => {
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    try {
      el.volume = Math.max(0, Math.min(1, volume ?? 1));
      el.muted = !soundOn;
    } catch {}
  }, [ref, volume, soundOn]);
};
